import { toast } from "sonner";
import { type ClipboardMachine, copyMachinesWithToast } from "@/lib/machine-clipboard";
import { machineHost } from "@/lib/machine-host";
import { TOAST_ERROR_DURATION_MS, TOAST_SUCCESS_DURATION_MS } from "@/lib/toast-durations";

/** Clipboard shape (name + ip) plus the ssh/gpu fields the warning toast reads. */
export interface AcquiredMachine extends ClipboardMachine {
  ssh_user?: string | null;
  ssh_setup_status?: string | null;
  gpu_cleanup_warning?: string | null;
}

interface AcquireStage {
  label: string;
  startMs: number;
}

// Time-gated cosmetic stages — the acquire endpoint has no progress stream.
const ACQUIRE_STAGES: AcquireStage[] = [
  { label: "Reserving the machine", startMs: 0 },
  { label: "Clearing GPU processes", startMs: 1500 },
  { label: "Setting up SSH access", startMs: 4000 },
  { label: "Finalizing handover", startMs: 12000 },
  { label: "Still rotating SSH keys", startMs: 30000 },
  { label: "Still working — bare-metal SSH can be slow", startMs: 90000 },
];

function stageLabel(elapsedMs: number): string {
  let label = "Reserving the machine";
  for (const stage of ACQUIRE_STAGES) {
    if (elapsedMs >= stage.startMs) label = stage.label;
  }
  return label;
}

// Unique toast ids; crypto.randomUUID() is unavailable on Fleet's plain-HTTP origin.
let acquireToastSeq = 0;

export interface AcquireToast {
  /** sonner toast id — pass to the finish* helpers to morph this toast in place. */
  id: string;
  /** The machine this toast tracks (used to label the error toast). */
  machineName: string;
  /** Stop the stage/elapsed timer. Call exactly once, before finishing. */
  stop: () => void;
}

/** True if an acquired machine carries a GPU-cleanup warning or a failed/skipped SSH setup. */
function hasAcquireWarning(machine: AcquiredMachine): boolean {
  const hasGpuWarning = !!machine.gpu_cleanup_warning;
  const hasSshWarning =
    !!machine.ssh_setup_status &&
    (machine.ssh_setup_status.startsWith("failed") ||
      machine.ssh_setup_status.startsWith("skipped"));
  return hasGpuWarning || hasSshWarning;
}

/** The Copy / "Copy all (N)" toast action. Explicit copy only — never auto-write the clipboard. */
function buildCopyAction(copyTargets: ClipboardMachine[]) {
  return {
    label: copyTargets.length > 1 ? `Copy all (${copyTargets.length})` : "Copy",
    onClick: (event: { preventDefault: () => void }) => {
      event.preventDefault();
      void copyMachinesWithToast(copyTargets);
    },
  };
}

/**
 * Open a non-blocking, stacking "Acquiring <name>" loading toast whose stage
 * label + elapsed advance on a timer. Resolve with finishAcquireSuccess/Error.
 * Non-empty `copyTargets` shows the Copy action immediately, before acquire finishes.
 */
export function startAcquireToast(
  machineName: string,
  copyTargets: ClipboardMachine[] = [],
): AcquireToast {
  acquireToastSeq += 1;
  const id = `acquire-${acquireToastSeq}`;
  const startedAt = performance.now();
  const copyAction = copyTargets.length > 0 ? buildCopyAction(copyTargets) : undefined;

  const render = () => {
    const elapsedMs = performance.now() - startedAt;
    toast.loading(`Acquiring ${machineName}`, {
      id,
      description: `${stageLabel(elapsedMs)} · ${Math.floor(elapsedMs / 1000)}s`,
      // Re-pass every render() tick or the action drops off the toast.
      action: copyAction,
      // Stay open + unswipeable while in flight; the finish* helpers restore both.
      duration: Number.POSITIVE_INFINITY,
      dismissible: false,
    });
  };

  render();
  // Coarse tick — stage thresholds are seconds apart.
  const timer = setInterval(render, 500);
  return {
    id,
    machineName,
    stop: () => clearInterval(timer),
  };
}

/**
 * Morph the acquiring toast into its success/warning result, in place.
 * `copyTargets` is what the Copy action copies — this machine by default, or
 * the whole batch so any toast can grab every machine just acquired.
 */
export function finishAcquireSuccess(
  id: string,
  machine: AcquiredMachine,
  copyTargets: ClipboardMachine[] = [machine],
): void {
  const hasSshWarning =
    !!machine.ssh_setup_status &&
    (machine.ssh_setup_status.startsWith("failed") ||
      machine.ssh_setup_status.startsWith("skipped"));
  const showWarning = !!machine.gpu_cleanup_warning || hasSshWarning;
  const sshUser = machine.ssh_user ?? "user";
  const sshHint = machineHost(machine) ? `ssh ${sshUser}@${machineHost(machine)}` : undefined;

  const copyAction = buildCopyAction(copyTargets);

  if (showWarning) {
    const lines = [
      sshHint,
      hasSshWarning && machine.ssh_setup_status ? `SSH: ${machine.ssh_setup_status}` : null,
      machine.gpu_cleanup_warning ? `GPU: ${machine.gpu_cleanup_warning}` : null,
    ].filter((line): line is string => !!line);
    toast.warning(`Acquired ${machine.name} with warnings`, {
      id,
      description: lines.join("\n"),
      duration: TOAST_ERROR_DURATION_MS,
      dismissible: true,
      action: copyAction,
    });
  } else {
    toast.success(`Acquired ${machine.name}`, {
      id,
      description: sshHint,
      duration: TOAST_SUCCESS_DURATION_MS,
      dismissible: true,
      action: copyAction,
    });
  }
}

/** Morph the acquiring toast into an error result, in place. */
export function finishAcquireError(id: string, machineName: string, message: string): void {
  toast.error(`Failed to acquire ${machineName}`, {
    id,
    description: message,
    duration: TOAST_ERROR_DURATION_MS,
    dismissible: true,
  });
}

/** One batch acquire result. `name` is resolved by the caller for failed rows (machine null). */
export interface BatchAcquireResult {
  ok: boolean;
  machine: AcquiredMachine | null;
  error: string | null;
  name: string;
}

/** Morph the batch toast in place into one consolidated success/warning/error result. */
export function finishBatchAcquire(id: string, results: BatchAcquireResult[]): void {
  const total = results.length;
  const acquired = results.flatMap((result) =>
    result.ok && result.machine ? [result.machine] : [],
  );
  const failed = results.filter((result) => !result.ok);
  const acquiredNames = acquired.map((machine) => machine.name);
  const copyTargets: ClipboardMachine[] = acquired.map((machine) => ({
    name: machine.name,
    ip: machineHost(machine),
  }));
  const copyAction = acquired.length > 0 ? buildCopyAction(copyTargets) : undefined;
  const warningCount = acquired.filter(hasAcquireWarning).length;

  if (failed.length === 0 && warningCount === 0) {
    toast.success(`Acquired ${acquired.length} machines`, {
      id,
      description: acquiredNames.join(", "),
      action: copyAction,
      duration: TOAST_SUCCESS_DURATION_MS,
      dismissible: true,
    });
  } else if (failed.length === 0) {
    toast.warning(`Acquired ${acquired.length} machines, ${warningCount} with warnings`, {
      id,
      description: acquiredNames.join(", "),
      action: copyAction,
      duration: TOAST_ERROR_DURATION_MS,
      dismissible: true,
    });
  } else if (acquired.length === 0) {
    toast.error(`Failed to acquire ${total} machines`, {
      id,
      description: failed
        .map((result) => `${result.name}: ${result.error ?? "Acquire failed"}`)
        .join("\n"),
      duration: TOAST_ERROR_DURATION_MS,
      dismissible: true,
    });
  } else {
    toast.warning(`Acquired ${acquired.length} of ${total} machines`, {
      id,
      description: `Failed: ${failed.map((result) => result.name).join(", ")}`,
      action: copyAction,
      duration: TOAST_ERROR_DURATION_MS,
      dismissible: true,
    });
  }
}
